import { useMemo, useCallback } from 'react';
import { usePaginatedQuery, useQuery } from 'convex/react';
import { api } from '../../api';
import { App } from '@/types/App';
import { deduplicateApps, sortAppsAlphabetically } from '@/utils/appUtils';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useUserDevice } from '@/hooks/useUserDevice';
import { useAppCategories } from '@/hooks/useAppCategories';

interface UseAvailableAppsOptions {
  searchQuery?: string;
}

export const useAvailableApps = (options: UseAvailableAppsOptions = {}) => {
  const { searchQuery = '' } = options;
  const { serial, source, isLoading: isSerialLoading } = useEffectiveSerial();
  const { device } = useUserDevice();
  const { deviceAppConfig } = useAppCategories();

  // Determine the effective app mode — default to most restrictive
  const appMode = (deviceAppConfig as any)?.appMode ?? 'whitelist';
  const isWhitelistMode = appMode === 'whitelist';
  
  // Determine if we're in search mode (3+ chars triggers server-side search)
  const isSearchMode = searchQuery.length >= 3;
  
  // Debug logging - only in development
  if (import.meta.env.DEV) {
    console.log('useAvailableApps - Resolution:', { 
      serial, 
      source, 
      isSerialLoading, 
      device: device?._id,
      searchQuery,
      isSearchMode,
      appMode,
      isWhitelistMode,
    });
  }
  
  // Determine if we should query - wait for serial to resolve from Convex before querying
  const shouldQuery = useMemo(() => !!serial && !isSerialLoading, [serial, isSerialLoading]);
  
  // Fetch apps using the filter endpoint with pagination.
  // Skip entirely in whitelist mode — only the static whitelist matters there.
  const { results, status, loadMore } = usePaginatedQuery(
    (api.apps.list as any).allowedByDeviceFilter,
    shouldQuery && !isWhitelistMode ? { 
      serial, 
      ...(isSearchMode ? { query: searchQuery } : {})
    } : "skip",
    { initialNumItems: 50 }
  );
  
  // Fetch statically allowed apps (non-paginated).
  // In whitelist mode this is the ONLY source of apps, so always fetch it.
  // In other modes only fetch during browse (search mode includes statics from backend).
  const staticResults = useQuery(
    (api.apps.list as any).allowedByDeviceStatic,
    shouldQuery && (isWhitelistMode || !isSearchMode) ? { serial } : "skip"
  );
  
  // Check if we've finished loading all pages
  const isFullyLoaded = useMemo(() => {
    if (isWhitelistMode) return staticResults !== undefined; // No pagination in whitelist mode
    if (isSearchMode) return true; // Search results are complete when returned
    return status === 'Exhausted' && staticResults !== undefined;
  }, [status, staticResults, isSearchMode, isWhitelistMode]);
  
  const isLoading = isWhitelistMode
    ? staticResults === undefined  // In whitelist mode, only wait for static results
    : (status === 'LoadingFirstPage' || results === undefined || (!isSearchMode && staticResults === undefined));

  // Debug logging - only in development
  if (import.meta.env.DEV) {
    console.log('useAvailableApps - Convex state:', {
      status,
      isLoading,
      isFullyLoaded,
      filterResultsCount: results?.length,
      isSearchMode
    });
  }

  // Helper function to transform raw app data
  const transformApp = (app: any, index: number): App => {
    const convexId = app.id;
    const id = convexId ? String(convexId) : `temp-${app.trackId || index}`;
    const name = app.name || app.trackName || app.title || 'Unknown App';
    const icon = app.iconUrl || app.icon || app.artworkUrl100 || '/placeholder.svg';
    const description = app.description || app.subtitle || '';
    const version = app.version || app.versionString || '';
    const appStoreId = String(app.trackId || '');
    const category = app.category || app.primaryGenreName || app.genre || undefined;
    
    return { id, name, icon, description, version, appStoreId, category };
  };

  // Helper to check if app should be hidden (system apps like webkit, safari)
  const isHiddenApp = (name: string): boolean => {
    const lowerName = name.toLowerCase().trim();
    return lowerName === 'webkit' || lowerName === 'safari' || lowerName.includes('webkit');
  };

  // Transform whitelisted apps (from static endpoint) - only for browse mode
  const whitelistedApps = useMemo(() => {
    if (isSearchMode) return []; // Not used in search mode
    if (!staticResults || !Array.isArray(staticResults)) return [];
    
    const transformed = staticResults
      .map((app: any, index: number) => transformApp(app, index))
      .filter(app => app.name && app.name.trim() !== '' && app.name !== '*')
      .filter(app => !isHiddenApp(app.name));
    
    return sortAppsAlphabetically(deduplicateApps(transformed));
  }, [staticResults, isSearchMode]);

  // Transform category apps (from paginated endpoint)
  const categoryApps = useMemo(() => {
    if (!results || !Array.isArray(results)) return [];
    
    const transformed = results
      .map((app: any, index: number) => transformApp(app, index))
      .filter(app => app.name && app.name.trim() !== '' && app.name !== '*')
      .filter(app => !isHiddenApp(app.name));
    
    if (isSearchMode) {
      // In search mode, backend already includes static apps and filters
      return sortAppsAlphabetically(deduplicateApps(transformed));
    }
    
    // In browse mode, remove duplicates that are already in whitelisted
    const whitelistedIds = new Set(whitelistedApps.map(a => a.id));
    const uniqueApps = transformed.filter(app => !whitelistedIds.has(app.id));
    
    return sortAppsAlphabetically(deduplicateApps(uniqueApps));
  }, [results, whitelistedApps, isSearchMode]);

  // Search results - only from backend (includes static apps when searching)
  const searchResults = useMemo(() => {
    if (!isSearchMode) return [];
    return categoryApps; // In search mode, categoryApps contains the search results
  }, [isSearchMode, categoryApps]);

  // Combined data for browse mode - properly merged and sorted
  const allApps = useMemo(() => {
    if (isSearchMode) {
      return searchResults;
    }
    
    // Merge whitelisted and category apps, then sort together
    const combined = [...whitelistedApps, ...categoryApps];
    return sortAppsAlphabetically(deduplicateApps(combined));
  }, [whitelistedApps, categoryApps, searchResults, isSearchMode]);

  // Combined data for backward compatibility
  const data = useMemo(() => {
    return { pages: [{ apps: allApps }] };
  }, [allApps]);

  // Extract available categories from loaded apps
  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    allApps.forEach(app => {
      if (app.category) {
        categories.add(app.category);
      }
    });
    return Array.from(categories).sort();
  }, [allApps]);

  // Handle loading and error states
  const error = (!results || results.length === 0) && !isLoading && shouldQuery && !isSearchMode
    ? new Error('No apps found') 
    : null;

  // Fetch next page function
  const fetchNextPage = useCallback(() => {
    if (import.meta.env.DEV) {
      console.log('🔄 useAvailableApps.fetchNextPage called', {
        status,
        serial,
        source,
        loadedSoFar: results?.length ?? 0,
      });
    }

    if (status === 'CanLoadMore') {
      loadMore(50);
    }
  }, [loadMore, status, serial, source, results?.length]);

  const retry = useCallback(() => {
    // Retry not implemented for static endpoint - but keeps API consistent
  }, []);

  return {
    data,
    whitelistedApps,
    categoryApps,
    allApps,
    searchResults,
    isLoading,
    isFullyLoaded,
    isSearchMode,
    availableCategories,
    error,
    fetchNextPage,
    hasNextPage: !isWhitelistMode && status === 'CanLoadMore',
    isFetchingNextPage: !isWhitelistMode && status === 'LoadingMore',
    usingDeviceSpecificAPI: shouldQuery,
    retry,
    retryCount: 0,
    debug: import.meta.env.DEV
      ? {
          status,
          serial,
          source,
          appMode,
          isWhitelistMode,
          filterResultsCount: results?.length ?? 0,
          staticResultsCount: Array.isArray(staticResults) ? staticResults.length : 0,
          isFullyLoaded,
          isSearchMode,
        }
      : undefined,
  };
};
